import { clsx } from 'clsx';
import { InputNumber } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import { useEffect, useState } from 'react';

import cls from './table-projected-cycle.module.css';
import { creditRiskHttp, TableProjectedCycleRow } from '@network';
import { LightTooltip } from '@components';
import { useWatch } from 'react-hook-form';

type Column = 'title' | 'value';
type Row = TableProjectedCycleRow;

interface Props {
  data: {rows: Row[]} | null,
  onChange: any;
  title: string;
  isFixed?: boolean;
}

export const TableProjectedCycle = ({ data, onChange, title, isFixed }: Props) => {
  const tableCrop = useWatch({ name: 'tableCrop' });
  const [defaultData, setDefaultData] = useState<{rows: Row[]}>({ rows: [] });

  useEffect(() => {
    (async () => {
      const dataDefault = await creditRiskHttp.getDefaultProjectedCycle();
      setDefaultData(dataDefault);
      if (!Array.isArray(data?.rows)) {
        setTimeout(() => void onChange(dataDefault), 500);
      }
    })();
  }, []);

  const onFieldChange = (val: number | null | string, key: Column, index: number) => {
    if (val === null) return;
    const nextRows = (data || defaultData)
      .rows.map((row, i) => i === index ? { ...row, [key]: val } : row);
    const nextData = { ...data, rows: nextRows };
    onChange(nextData);
  };

  const setField = (fieldTitle: string, value: number | string) => {
    const nextRows = (data || defaultData).rows.map(row => {
      if (row.title === fieldTitle) {
        return { ...row, value };
      }
      return row;
    });
    const nextData = { ...data, rows: nextRows };
    onChange(nextData);
  };

  useEffect(() => {
    if (!Array.isArray(tableCrop?.rows)) return;
    let maxYear = 0;
    let incomeTotal = 0;
    tableCrop.rows.forEach((row) => {
      if (row.year > maxYear) {
        maxYear = row.year;
        incomeTotal = row.incomeTotal;
      }
    });
    setField('Global Sales', incomeTotal);
  }, [tableCrop]);

  if (!Array.isArray(data?.rows)) {
    setTimeout(() => void onChange(defaultData), 500);
  }

  const rows = data?.rows || [] as Row[];

  return (
    <div className={cls.block}>
      <div className={cls.table}>
        <div className={cls.header}>
          <div className={cls.headerRow}>
            <div className={cls.headerCol}>{title}</div>
          </div>
        </div>
        <div className={cls.body}>
          {rows.map((row, index) => (
            <div key={index}>
              {typeof row.separator === 'string' && (
                <div className={cls.separator}>{row.separator}</div>
              )}
              <div className={clsx(cls.row, cls.rowColored, row.disabled && cls.rowDisabled)}>
                <div className={clsx(cls.col, cls.colTitle)}>
                  {row.title}
                </div>
                <div className={clsx(cls.col, !row.disabled && cls.colEditable)}>
                  <Input row={row}
                    field={'value'}
                    onChange={onFieldChange}
                    index={index}
                    isFixed={isFixed || row.disabled}
                  />
                </div>
                {row.tooltip && (
                  <LightTooltip title={row.tooltip} extraWidth>
                    <div className={cls.tooltip}><InfoCircleFilled /></div>
                  </LightTooltip>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface InputProps {
  row: Row;
  field: Column;
  onChange?: any;
  index?: number;
  isFixed?: boolean;
  forcedValue?: number | string;
}

const Input = ({ row, field, onChange, index, isFixed, forcedValue }: InputProps) => {
  const value = forcedValue ?? row[field];
  const prefix = row.format === 'money' ? '$ ' : '';
  const postfix = row.format === 'percent' ? ' %' : '';

  return (
    <InputNumber
      min={0}
      max={1000000000}
      onBeforeInputCapture={e => {
        const value = e.currentTarget.value;
        if (value && value.includes('.')) {
          const [, second] = value.split('.');
          if (second.length >= 2) {
            e.stopPropagation();
            e.preventDefault();
          }
        }
      }}
      formatter={(val) => `${prefix}${val}${postfix}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
      parser={(val) => {
        let nextVal: number = val ? +val.replace(/\$\s?|\s?\%|(,*)/g, '') : 0;
        if (row.title === 'Adjusting Variable') {
          if (nextVal < 0) nextVal = 0;
          if (nextVal > 100) nextVal = 100;
        }

        return nextVal;
      }}
      value={value}
      bordered={false}
      onChange={val => onChange(val, field, index)}
      className={cls.input}
      disabled={isFixed}
      controls={false}
    />
  );
};
